import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { ContractorFormDialog } from "../components/ContractorFormDialog";
import { ContractorDetailDialog } from "../components/ContractorDetailDialog";
import { RolodexIcon } from "../components/icons";
import { AppHeader } from "../components/AppHeader";
import { useEffectiveRole } from "../lib/view-as-context";

interface Contractor {
  _id: string;
  name: string;
  serviceType: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

type Role = "landlord" | "tenant" | null | undefined;

export default function Contractors() {
  const property = useProperty();
  const realRole = useQuery(
    anyApi.memberships.getMyRoleOnProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as Role;
  const role = useEffectiveRole(realRole);
  const contractors = useQuery(anyApi.contractors.list, {}) as
    | Contractor[]
    | undefined;

  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Contractor | null>(null);

  if (role === "tenant") {
    return <Navigate to={`/${property?.slug}/dashboard`} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2.5 text-3xl font-semibold tracking-tight text-slate-900">
              <RolodexIcon className="text-slate-500 h-6 w-6" />
              Rolodex
            </h1>
            <p className="mt-2 text-slate-600">
              Every vendor across all properties. Click any contractor to see
              their work history.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
          >
            Add contractor
          </button>
        </div>

        <Body
          contractors={contractors}
          onSelect={setSelected}
          onAdd={() => setAddOpen(true)}
        />
      </main>

      <ContractorFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
      <ContractorDetailDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        contractor={selected}
      />
    </div>
  );
}

function Body({
  contractors,
  onSelect,
  onAdd,
}: {
  contractors: Contractor[] | undefined;
  onSelect: (c: Contractor) => void;
  onAdd: () => void;
}) {
  if (contractors === undefined) return <SkeletonGrid />;
  if (contractors.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center">
        <p className="text-sm font-medium text-slate-900">
          No contractors yet.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Add the vendors you call regularly so they show up in the maintenance
          scheduler.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
        >
          Add your first contractor
        </button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {contractors.map((c) => (
        <Card key={c._id} contractor={c} onClick={() => onSelect(c)} />
      ))}
    </div>
  );
}

function Card({
  contractor,
  onClick,
}: {
  contractor: Contractor;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-sm transition-all"
    >
      <p className="text-sm font-semibold text-slate-900">{contractor.name}</p>
      <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-slate-500">
        {contractor.serviceType}
      </p>
      {(contractor.email || contractor.phone) && (
        <p className="mt-3 text-xs text-slate-500 truncate">
          {contractor.phone ?? contractor.email}
        </p>
      )}
    </button>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-xl border border-slate-200 bg-white p-5 animate-pulse space-y-2"
        >
          <div className="h-4 w-2/3 rounded bg-slate-100" />
          <div className="h-3 w-1/3 rounded bg-slate-100" />
          <div className="h-3 w-1/2 rounded bg-slate-100 mt-3" />
        </div>
      ))}
    </div>
  );
}

